/**
 * Bildkonvertierung zu WebP
 * Konvertiert Bilder automatisch in das WebP-Format für bessere Kompression
 */

/**
 * Konvertiert ein Bild in WebP-Format
 * @param file - Die ursprüngliche Bilddatei
 * @param quality - Qualität (0-1), Standard: 0.85 (85%)
 * @param maxWidth - Maximale Breite in Pixeln (optional, behält Seitenverhältnis)
 * @param maxHeight - Maximale Höhe in Pixeln (optional, behält Seitenverhältnis)
 * @returns Promise<File> - Die konvertierte WebP-Datei
 */
export async function convertToWebP(
  file: File,
  quality: number = 0.85,
  maxWidth?: number,
  maxHeight?: number
): Promise<File> {
  // Prüfe, ob es sich um ein Bild handelt
  if (!file.type.startsWith('image/')) {
    throw new Error('Datei ist kein Bild')
  }

  // Prüfe, ob WebP bereits unterstützt wird
  if (!isWebPSupported()) {
    // Fallback: Wenn WebP nicht unterstützt wird, gebe die Originaldatei zurück
    // (sollte in modernen Browsern nicht vorkommen)
    return file
  }

  return new Promise((resolve, reject) => {
    const img = new Image()
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      reject(new Error('Canvas-Kontext konnte nicht erstellt werden'))
      return
    }

    img.onload = () => {
      try {
        // Berechne neue Dimensionen bei Bedarf
        let width = img.width
        let height = img.height

        if (maxWidth && width > maxWidth) {
          height = (height * maxWidth) / width
          width = maxWidth
        }

        if (maxHeight && height > maxHeight) {
          width = (width * maxHeight) / height
          height = maxHeight
        }

        // Setze Canvas-Dimensionen
        canvas.width = width
        canvas.height = height

        // Optimiere Canvas-Rendering für bessere Qualität
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        
        // Zeichne Bild auf Canvas (mit Qualitätsoptimierung)
        ctx.drawImage(img, 0, 0, width, height)

        // Konvertiere zu WebP
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Konvertierung zu WebP fehlgeschlagen'))
              return
            }

            // Erstelle neue File mit WebP-Extension
            const fileName = file.name.replace(/\.[^/.]+$/, '') + '.webp'
            const webpFile = new File([blob], fileName, {
              type: 'image/webp',
              lastModified: Date.now()
            })

            resolve(webpFile)
          },
          'image/webp',
          quality
        )
      } catch (error) {
        reject(error)
      }
    }

    img.onerror = () => {
      reject(new Error('Bild konnte nicht geladen werden'))
    }

    // Lade Bild (verwende Object URL für bessere Kompatibilität)
    img.src = URL.createObjectURL(file)
  })
}

/**
 * Prüft, ob WebP vom Browser unterstützt wird
 */
function isWebPSupported(): boolean {
  const canvas = document.createElement('canvas')
  canvas.width = 1
  canvas.height = 1
  return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0
}

/**
 * Prüft, ob eine Datei bereits WebP ist
 */
export function isWebP(file: File): boolean {
  return file.type === 'image/webp' || file.name.toLowerCase().endsWith('.webp')
}

/**
 * Konvertiert ein Bild nur, wenn es noch nicht WebP ist
 * @param file - Die Bilddatei
 * @param quality - Qualität (0-1), Standard: 0.85
 * @param maxWidth - Maximale Breite (optional)
 * @param maxHeight - Maximale Höhe (optional)
 * @returns Promise<File> - WebP-Datei oder Original, falls bereits WebP
 */
export async function convertToWebPIfNeeded(
  file: File,
  quality: number = 0.85,
  maxWidth?: number,
  maxHeight?: number
): Promise<File> {
  // Wenn bereits WebP, gebe Original zurück
  if (isWebP(file)) {
    return file
  }

  return convertToWebP(file, quality, maxWidth, maxHeight)
}

/**
 * Optimiert ein Bild mit intelligenten Einstellungen basierend auf der Dateigröße
 * @param file - Die Bilddatei
 * @returns Promise<File> - Optimiertes WebP-Bild
 */
export async function optimizeImage(file: File): Promise<File> {
  // Wenn bereits WebP und klein genug, gebe zurück
  if (isWebP(file) && file.size < 500 * 1024) {
    return file
  }

  // Bestimme Qualität basierend auf Dateigröße
  // Höhere Qualitätswerte für bessere Bildqualität
  let quality = 0.90 // Standard-Qualität erhöht von 0.85 auf 0.90
  let maxWidth: number | undefined = undefined
  let maxHeight: number | undefined = undefined

  // Aggressivere Größenreduzierung für sehr große Bilder
  if (file.size > 10 * 1024 * 1024) {
    // 10+ MB: Starke Reduzierung
    quality = 0.70
    maxWidth = 1920  // Full HD
    maxHeight = 1080
  } else if (file.size > 5 * 1024 * 1024) {
    // 5-10 MB: Moderate Reduzierung
    quality = 0.80
    maxWidth = 2560  // QHD
    maxHeight = 1440
  } else if (file.size > 2 * 1024 * 1024) {
    // 2-5 MB: Leichte Reduzierung
    quality = 0.85
    maxWidth = 3840  // 4K
    maxHeight = 2160
  }
  // Unter 2 MB: Nur Format-Konvertierung mit hoher Qualität (0.90)

  return convertToWebPIfNeeded(file, quality, maxWidth, maxHeight)
}

/**
 * Generiert eine optimierte Bild-URL mit optionalen Parametern
 * @param url - Die Original-Bild-URL
 * @param options - Optionale Parameter für Bildoptimierung
 * @returns Optimierte URL
 */
export function getOptimizedImageUrl(
  url: string, 
  options?: {
    width?: number
    height?: number
    quality?: number
  }
): string {
  try {
    const urlObj = new URL(url)
    
    // Für Firebase Storage URLs: Parameter hinzufügen
    if (urlObj.hostname.includes('firebasestorage.googleapis.com')) {
      if (options?.width) {
        urlObj.searchParams.set('width', options.width.toString())
      }
      if (options?.height) {
        urlObj.searchParams.set('height', options.height.toString())
      }
      if (options?.quality) {
        urlObj.searchParams.set('quality', options.quality.toString())
      }
    }
    
    return urlObj.toString()
  } catch (error) {
    // Bei Fehler gebe Original-URL zurück
    return url
  }
}

/**
 * Erstellt ein Responsive-Bild-Set (srcset) für verschiedene Bildschirmgrößen
 * @param url - Die Original-Bild-URL
 * @param sizes - Array von Breiten für das srcset
 * @returns srcset-String für das img-Tag
 */
export function createResponsiveSrcSet(
  url: string,
  sizes: number[] = [640, 768, 1024, 1280, 1536, 1920]
): string {
  return sizes
    .map(width => `${getOptimizedImageUrl(url, { width })} ${width}w`)
    .join(', ')
}

/**
 * Berechnet die Dateigröße-Reduktion in Prozent
 * @param originalSize - Ursprüngliche Dateigröße in Bytes
 * @param newSize - Neue Dateigröße in Bytes
 * @returns Reduktion in Prozent
 */
export function calculateSizeReduction(originalSize: number, newSize: number): number {
  if (originalSize === 0) return 0
  return Math.round((1 - newSize / originalSize) * 100)
}

/**
 * Formatiert Dateigröße für lesbare Darstellung
 * @param bytes - Dateigröße in Bytes
 * @returns Formatierte Dateigröße (z.B. "2.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
}



